import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { subscribe, dismiss } from '../utils/toast';

/**
 * ToastHost — mount ONCE, globally. Subscribes to the toast store and renders:
 *   - Confirm modals (full-screen, blocking)
 *   - Rich cards (top-right stack, auto-dismiss or sticky)
 *   - Progress cards (sticky, with a live bar)
 */
export default function ToastHost() {
  const [toasts, setToasts] = useState([]);
  useEffect(() => subscribe(setToasts), []);

  const confirms = toasts.filter(t => t.confirm);
  const cards    = toasts.filter(t => !t.confirm);

  return createPortal(
    <>
      {/* Rich cards / progress cards — stacked top-right */}
      <div style={stack}>
        {cards.map(t => (
          <ToastCard key={t.id} t={t} onClose={() => dismiss(t.id)} />
        ))}
      </div>
      {/* Confirm modals — render on top of everything */}
      {confirms.map(c => <ConfirmModal key={c.id} c={c} />)}
    </>,
    document.body
  );
}

/* ─────────────────────── Rich / progress card ─────────────────────── */
function ToastCard({ t, onClose }) {
  const kind = t.kind || 'info';
  const accent = ACCENTS[kind] || ACCENTS.info;

  return (
    <div style={{
      ...card,
      borderColor: accent.border,
      boxShadow: `0 12px 34px rgba(0,0,0,0.5), 0 0 0 1px ${accent.glow}`,
      animation: 'tSlide 0.24s ease',
    }}>
      <div style={{
        ...head,
        background: `linear-gradient(180deg, ${accent.headBg}, transparent)`,
      }}>
        <span style={{ ...dot, background: accent.color }} />
        <span style={{ fontSize: 11, color: accent.color, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
          {kind}
        </span>
        <button onClick={onClose} style={hdrClose} title="Dismiss">×</button>
      </div>
      {(t.title || t.body) && (
        <div style={body}>
          {t.title && <div style={bodyTitle}>{t.title}</div>}
          {t.body &&  <div style={bodyDesc}>{t.body}</div>}
        </div>
      )}
      {t.progress && (
        <div style={{ padding: '4px 14px 12px' }}>
          <div style={{ ...pbarTitle, color: accent.color }}>
            <span>{t.progress.meta || ''}</span>
            <span style={{ fontFamily: 'ui-monospace, monospace' }}>{Math.round(t.progress.pct)}%</span>
          </div>
          <div style={pbarOuter}>
            <div style={{
              ...pbarFill,
              width: `${Math.min(100, Math.max(0, t.progress.pct))}%`,
              background: `linear-gradient(90deg, ${accent.color}, ${accent.color2})`,
            }}/>
          </div>
        </div>
      )}
      {t.actions && t.actions.length > 0 && (
        <div style={actions}>
          {t.actions.map((a, i) => (
            <button key={i}
              onClick={() => { try { a.onClick && a.onClick(); } finally { if (!a.keepOpen) onClose(); } }}
              style={a.variant === 'primary' ? actionPrimary(accent) : (a.variant === 'dismiss' ? actionDismiss : actionSecondary)}
            >{a.label}</button>
          ))}
        </div>
      )}

      <style>{`@keyframes tSlide { from { transform: translateX(18px); opacity: 0; } to { transform: none; opacity: 1; } }`}</style>
    </div>
  );
}

/* ─────────────────────── Confirm modal ─────────────────────── */
function ConfirmModal({ c }) {
  return (
    <div style={overlay}>
      <div style={{ ...modal, animation: 'mPop 0.2s cubic-bezier(.2,1.1,.4,1.2)' }}>
        <div style={modalIconWrap}>
          <div style={{ ...modalIcon, background: c.danger ? 'rgba(239,68,68,0.16)' : 'rgba(245,158,11,0.16)', color: c.danger ? '#EF4444' : '#F59E0B' }}>
            {c.danger ? '⚠' : '?'}
          </div>
        </div>
        <h3 style={modalTitle}>{c.title}</h3>
        {c.body && <p style={modalBody}>{c.body}</p>}
        <div style={modalRow}>
          <button onClick={() => c.onResolve(false)} style={modalCancel}>{c.cancelLabel}</button>
          <button onClick={() => c.onResolve(true)}  style={{ ...modalConfirm, color: c.danger ? '#EF4444' : '#00E0FF' }}>
            {c.confirmLabel}
          </button>
        </div>
      </div>
      <style>{`@keyframes mPop { from { transform: scale(0.96); opacity: 0; } to { transform: none; opacity: 1; } }`}</style>
    </div>
  );
}

/* ─────────────────────── styles ─────────────────────── */
const ACCENTS = {
  success: { color: '#10B981', color2: '#34D399', border: 'rgba(16,185,129,0.35)',  glow: 'rgba(16,185,129,0.12)',  headBg: 'rgba(16,185,129,0.10)' },
  info:    { color: '#3B8FF4', color2: '#60A5FA', border: 'rgba(59,143,244,0.35)',  glow: 'rgba(59,143,244,0.12)',  headBg: 'rgba(59,143,244,0.10)' },
  warn:    { color: '#F59E0B', color2: '#FBBF24', border: 'rgba(245,158,11,0.35)',  glow: 'rgba(245,158,11,0.12)',  headBg: 'rgba(245,158,11,0.10)' },
  error:   { color: '#EF4444', color2: '#FB7185', border: 'rgba(239,68,68,0.35)',   glow: 'rgba(239,68,68,0.12)',   headBg: 'rgba(239,68,68,0.10)' },
};

const stack = {
  position: 'fixed', top: 16, right: 16, zIndex: 10000,
  display: 'flex', flexDirection: 'column', gap: 10,
  maxWidth: 360, width: 'calc(100% - 32px)',
  pointerEvents: 'none',
};
const card = {
  background: 'rgba(11,15,23,0.98)',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 12,
  overflow: 'hidden',
  pointerEvents: 'auto',
};
const head = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '8px 12px',
  borderBottom: '1px solid rgba(255,255,255,0.06)',
};
const dot = { width: 8, height: 8, borderRadius: '50%' };
const hdrClose = {
  marginLeft: 'auto',
  width: 22, height: 22, borderRadius: 5, padding: 0,
  border: '1px solid rgba(255,255,255,0.06)', background: 'transparent',
  color: '#94A3B8', cursor: 'pointer', fontSize: 13,
};
const body = { padding: '10px 14px 6px' };
const bodyTitle = { fontSize: 13.5, fontWeight: 600, color: '#E5E7EB', marginBottom: 3 };
const bodyDesc  = { fontSize: 12.5, color: '#94A3B8', lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word' };

const pbarTitle = { display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 6 };
const pbarOuter = { height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 3, overflow: 'hidden' };
const pbarFill  = { height: '100%', borderRadius: 3, transition: 'width 0.25s ease' };

const actions = {
  display: 'flex', gap: 6,
  padding: '8px 12px',
  borderTop: '1px solid rgba(255,255,255,0.06)',
  background: 'rgba(255,255,255,0.015)',
};
function actionPrimary(acc) {
  return {
    padding: '5px 11px', fontSize: 11.5, borderRadius: 5, fontWeight: 600,
    border: `1px solid ${acc.border}`, background: acc.glow, color: acc.color, cursor: 'pointer',
  };
}
const actionSecondary = {
  padding: '5px 11px', fontSize: 11.5, borderRadius: 5,
  border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.04)', color: '#E5E7EB', cursor: 'pointer',
};
const actionDismiss = { ...actionSecondary, marginLeft: 'auto', color: '#64748B' };

const overlay = {
  position: 'fixed', inset: 0, zIndex: 10001,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)',
};
const modal = {
  width: 320, background: 'rgba(15,19,28,0.98)',
  border: '1px solid rgba(255,255,255,0.10)', borderRadius: 14,
  boxShadow: '0 20px 60px rgba(0,0,0,0.7)', overflow: 'hidden',
};
const modalIconWrap = { display: 'flex', justifyContent: 'center', padding: '20px 20px 6px' };
const modalIcon = {
  width: 48, height: 48, borderRadius: '50%',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontSize: 22, fontWeight: 700,
};
const modalTitle = { margin: '8px 20px 4px', fontSize: 15, textAlign: 'center', fontWeight: 600, color: '#E5E7EB' };
const modalBody  = { margin: '0 20px 18px', fontSize: 12.5, textAlign: 'center', color: '#94A3B8', lineHeight: 1.5 };
const modalRow = {
  display: 'grid', gridTemplateColumns: '1fr 1fr',
  borderTop: '1px solid rgba(255,255,255,0.08)',
};
const modalCancel = { padding: '12px 0', background: 'transparent', border: 'none',
  borderRight: '1px solid rgba(255,255,255,0.08)', color: '#94A3B8', fontSize: 13, cursor: 'pointer' };
const modalConfirm = { padding: '12px 0', background: 'transparent', border: 'none',
  fontSize: 13, fontWeight: 600, cursor: 'pointer' };
